import { type CommentGroupObject as MapCommentGroupObject, MapStyle } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { Point } from '../SurfaceBase';
import { commentExtraSpacing, commentLineWidth, commentRadius, getCircleBgColor } from './common';

export default class CommentObject extends CanvasObject {
  commentGroup: MapCommentGroupObject;
  private point: Point;
  private markerColor: string;

  constructor(id: string, surface: SurfaceBase, commentGroup: MapCommentGroupObject) {
    super(id, surface);
    this.commentGroup = commentGroup;
    this.point = this.surface.toScreenCoords({
      lng: commentGroup.geometry.coordinates[0],
      lat: commentGroup.geometry.coordinates[1],
    });
    this.markerColor = getCircleBgColor({
      initial: commentGroup.createdBy.name.charAt(0).toUpperCase(),
    });
  }

  refreshScreenCoords(): void {
    this.point = this.surface.toScreenCoords({
      lng: this.commentGroup.geometry.coordinates[0],
      lat: this.commentGroup.geometry.coordinates[1],
    });
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = commentLineWidth;
    ctx.beginPath();
    // hit area is larger than the radius
    ctx.arc(
      this.point.x + commentExtraSpacing,
      this.point.y + commentExtraSpacing,
      commentRadius * 1.5,
      0,
      2 * Math.PI,
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  render(ctx: CanvasRenderingContext2D): void {
    // marker shell
    ctx.beginPath();
    ctx.strokeStyle = this.markerColor;
    ctx.lineWidth = commentLineWidth;
    ctx.fillStyle = '#ffffff';
    ctx.moveTo(this.point.x, this.point.y);
    ctx.lineTo(this.point.x, this.point.y + commentExtraSpacing);
    const radius = commentExtraSpacing; // Arc radius
    const startAngle = -3.4; // Starting point on circle
    const endAngle = Math.PI + Math.PI / 2; // End point on circle
    const counterclockwise = true; // Draw counterclockwise
    ctx.arc(
      this.point.x + commentExtraSpacing,
      this.point.y + commentExtraSpacing,
      radius,
      startAngle,
      endAngle,
      counterclockwise,
    );
    ctx.fill();
    ctx.closePath();
    ctx.stroke();

    // circle
    ctx.beginPath();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = this.markerColor;
    ctx.lineWidth = commentLineWidth;
    ctx.fillStyle = this.markerColor;
    ctx.arc(this.point.x + commentExtraSpacing, this.point.y + commentExtraSpacing, commentRadius, 0, 2 * Math.PI);
    ctx.fill();

    // text
    ctx.font = 'bold 14pt Inter';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      this.commentGroup.createdBy.name ? this.commentGroup.createdBy.name.charAt(0).toUpperCase() : '',
      this.point.x + commentExtraSpacing,
      this.point.y + commentExtraSpacing,
    );
    ctx.closePath();
    ctx.stroke();

    const store = this.getStore();
    if (
      !store.currentMap.isLoading &&
      (store.currentMap.baseMap === MapStyle.satellite || store.currentMap.baseMap === MapStyle.dark)
    ) {
      ctx.strokeStyle = 'black';
      ctx.fillStyle = 'white';
    } else {
      ctx.strokeStyle = 'white';
      ctx.fillStyle = 'black';
    }

    ctx.font = 'bold 1.2rem sans-serif';
    ctx.textBaseline = 'middle';
  }
}
